import React, { useEffect, useState } from 'react';
import './DeviceStatusModal.css';
import { getMessages, sendMessage, syncMessages, getMessagingDevices } from '../../services/api/messageApi';
import { getPositions } from '../../services/api/deviceApi';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faUpload, faDownload, faCheckCircle, faShippingFast, faSatelliteDish } from '@fortawesome/free-solid-svg-icons';
import { STATUS_COMMAND_CONFIG } from '../../config/statusConfig';
import { setDeviceStatus } from '../../services/api/traccarManagerApi';

const DeviceStatusModal = ({ device, onClose, isReadOnly = false }) => {
    const [history, setHistory] = useState([]);
    const [sending, setSending] = useState(false);
    const [statusProcessing, setStatusProcessing] = useState(false);
    const [nceDevices, setNceDevices] = useState([]);
    const [localStatus, setLocalStatus] = useState(null); // 'SOS', 'OK'
    const [lastLocalUpdate, setLastLocalUpdate] = useState(0);

    // Determination of current active status, prioritising local optimistic update for 5 seconds
    // CLEAR is never returned — clearing means no status
    const getCurrentStatus = () => {
        const now = Date.now();
        if (localStatus && (now - lastLocalUpdate < 5000)) {
            return localStatus;
        }
        return device?.attributes?.CarStatus || (device?.isSosActive ? 'SOS' : null);
    };

    const currentStatus = getCurrentStatus();
    const isSosActive = currentStatus === 'SOS';

    const deviceName = device?.deviceName || device?.name || 'Unknown Device';
    const deviceId = device?.deviceId || device?.id;

    const [activeSections, setActiveSections] = useState({
        now: true,
        lastHour: false,
        others: false
    });

    const [isOnline, setIsOnline] = useState(device?.status === 'online');

    useEffect(() => {
        const checkStatus = async () => {
            let online = device?.status === 'online' || (new Date() - new Date(device?.lastUpdate) < 15 * 60 * 1000);
            const now = new Date();
            const timeLimit = new Date(now.getTime() - 15 * 60 * 1000); // 15 mins

            // Check History
            if (history.length > 0) {
                const recentIncoming = history.find(msg => {
                    if (msg.status !== 'RECEIVED') return false;
                    let timeStr = (msg.created_at || msg.createdAt || '').replace(' ', 'T');
                    if (timeStr && !timeStr.includes('Z') && !timeStr.includes('+') && !timeStr.includes('-')) {
                        timeStr += '-06:00';
                    }
                    const msgDate = new Date(timeStr);
                    return msgDate > timeLimit;
                });
                if (recentIncoming) online = true;
            }

            // Check Position if still offline
            if (!online && deviceId) {
                try {
                    const positions = await getPositions(deviceId, { limit: 1 });
                    if (positions && positions.length > 0) {
                        const lastPos = positions[0];
                        // Traccar usually provides deviceTime, fixTime, serverTime. prefer deviceTime or fixTime
                        const posTimeStr = lastPos.deviceTime || lastPos.fixTime || lastPos.serverTime;
                        if (posTimeStr) {
                            const posTime = new Date(posTimeStr);
                            if (posTime > timeLimit) {
                                online = true;
                                console.log('DeviceStatusModal: Online via position', posTime);
                            }
                        }
                    }
                } catch (err) {
                    console.warn('Failed to check position status', err);
                }
            }

            setIsOnline(online);
        };

        checkStatus();
    }, [device, history, deviceId]);

    useEffect(() => {
        // Fetch 1NCE devices to map ICCID
        getMessagingDevices()
            .then(data => setNceDevices(data || []))
            .catch(err => console.error('Failed to fetch 1NCE devices', err));
    }, []);

    useEffect(() => {
        if (deviceId && nceDevices.length > 0) {
            loadHistory();
        } else if (deviceId && nceDevices.length === 0) {
            // Still try to load local history if we have deviceId but no NCE devices confirmed yet
            loadHistory();
        }
    }, [deviceId, nceDevices]);

    // Find the messaging device record (1NCE/Twilio) matching the current Traccar device
    const findMsgDevice = () => {
        const traccarId = String(deviceId).trim();
        return nceDevices.find(n => String(n.DeviceIDTraccar || '').trim() === traccarId) || null;
    };

    const loadHistory = async () => {
        try {
            const msgDevice = findMsgDevice();
            if (msgDevice) {
                const data = await syncMessages(msgDevice, 50);
                setHistory(data || []);
            } else {
                throw new Error('No messaging device found for sync');
            }
        } catch (err) {
            console.warn('Sync failed, falling back to local history', err);
            try {
                const msgDevice = findMsgDevice();
                const fallbackDevice = msgDevice || { id: deviceId, model: '' };
                const localData = await getMessages({ device: fallbackDevice, limit: 50 });
                setHistory(localData || []);
            } catch (localErr) {
                console.error('Failed to load local history', localErr);
            }
        }
    };

    const handleAskStatus = async () => {
        if (!deviceId) return;
        setSending(true);
        try {
            const msgDevice = findMsgDevice();
            if (!msgDevice) {
                alert('No messaging device configuration found for this device');
                return;
            }
            const response = await sendMessage(msgDevice, STATUS_COMMAND_CONFIG.statusCommand);
            console.log('DeviceStatusModal: Send success', response);

            // Wait a sec for backend processing then refresh
            await new Promise(r => setTimeout(r, 1000));
            await loadHistory();
        } catch (err) {
            console.error('Failed to send status command', err);
            alert(`Failed to send: ${err.message}`);
        } finally {
            setSending(false);
        }
    };

    const toggleSection = (section) => {
        setActiveSections(prev => ({
            ...prev,
            [section]: !prev[section]
        }));
    };

    const handleSetStatus = async (status) => {
        if (!deviceId) return;

        const confirmMsg = status === 'CLEAR'
            ? 'Are you sure you want to clear current status?'
            : `Set device status to ${status}?`;

        if (!window.confirm(confirmMsg)) return;

        setStatusProcessing(true);
        try {
            await setDeviceStatus(deviceId, status);
            // Optimistic update: CLEAR means no status — don't show a stale badge
            if (status !== 'CLEAR') {
                setLocalStatus(status);
                setLastLocalUpdate(Date.now());
            } else {
                setLocalStatus(null);
            }
        } catch (err) {
            console.error('Failed to set status', err);
            alert(`Failed to set status: ${err.message}`);
        } finally {
            setStatusProcessing(false);
        }
    };

    // Helper to group messages
    const groupMessages = (msgs) => {
        const now = new Date();
        const oneHourAgo = new Date(now.getTime() - 60 * 60 * 1000);

        const groups = {
            now: [],
            lastHour: [],
            others: []
        };

        msgs.forEach(msg => {
            // Skip messages with status "Array" as per user request
            if (msg.status === 'Array') return;

            // Fix timezone: Server appears to be in UTC-6 (CST/MDT).
            // We append -06:00 to treat it as that specific timezone, converting it to local time correctly.
            // Ensure we handle potential missing created_at
            let timeStr = (msg.created_at || msg.createdAt || '').replace(' ', 'T');
            if (timeStr && !timeStr.includes('Z') && !timeStr.includes('+') && !timeStr.includes('-')) {
                timeStr += '-06:00';
            }

            const msgDate = new Date(timeStr);
            // Attach fixed date to msg for TimelineItem to use
            msg.fixedDate = msgDate;

            // Logic for "Now" - last 15 mins
            const fifteenMinsAgo = new Date(now.getTime() - 15 * 60 * 1000);

            if (msgDate > fifteenMinsAgo) {
                groups.now.push(msg);
            } else if (msgDate > oneHourAgo) {
                groups.lastHour.push(msg);
            } else {
                groups.others.push(msg);
            }
        });

        return groups;
    };

    const grouped = groupMessages(history);

    if (!device) return null;

    return (
        <div className="device-status-modal-overlay">
            <div className="device-status-modal">
                {/* Header */}
                <header className="dsm-header">
                    <div className="dsm-header-left">
                        <div className="dsm-icon-box">
                            <FontAwesomeIcon icon={faShippingFast} size="lg" />
                        </div>
                        <div>
                            <h1 className="dsm-title">{deviceName}</h1>
                            <div className="dsm-status-badge">
                                <div className="dsm-status-dot-container">
                                    {isOnline && <span className="dsm-status-dot-ping"></span>}
                                    <span className={`dsm-status-dot ${!isOnline ? 'offline' : ''}`} style={{ backgroundColor: isOnline ? 'var(--status-green-500)' : '#94a3b8' }}></span>
                                </div>
                                <span className="dsm-status-text" style={{ color: isOnline ? 'var(--status-green-600)' : '#64748b' }}>
                                    {isOnline ? 'Device Online' : 'Offline'}
                                </span>
                            </div>
                            {/* SMS Status Badge */}
                            {currentStatus && (
                                <div className="dsm-sms-status-row" style={{
                                    display: 'flex', alignItems: 'center', gap: '6px',
                                    marginTop: '4px', flexWrap: 'wrap'
                                }}>
                                    <span style={{
                                        display: 'inline-flex', alignItems: 'center', gap: '4px',
                                        padding: '2px 10px', borderRadius: '999px', fontSize: '11px', fontWeight: 700,
                                        letterSpacing: '0.05em',
                                        backgroundColor: isSosActive ? '#fee2e2' : '#dcfce7',
                                        color: isSosActive ? '#b91c1c' : '#15803d',
                                        border: `1px solid ${isSosActive ? '#fca5a5' : '#86efac'}`,
                                    }}>
                                        {isSosActive ? '🚨 SOS' : '✅ OK'}
                                    </span>
                                    {device?.attributes?.lastSmsTime && (
                                        <span style={{ fontSize: '10px', color: '#94a3b8' }}>
                                            SMS: {new Date(device.attributes.lastSmsTime).toLocaleString()}
                                        </span>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                    <button className="dsm-close-btn" onClick={onClose}>
                        <FontAwesomeIcon icon={faTimes} className="text-xl" color="#475569" />
                    </button>
                </header>

                {/* Main Content */}
                <main className="dsm-main" style={{ paddingBottom: '2rem' }}>

                    {/* Section NOW */}
                    {grouped.now.length > 0 && (
                        <section className="dsm-section">
                            <div className="dsm-section-header">
                                <span className="dsm-section-title">Now</span>
                                <div className="dsm-divider"></div>
                            </div>
                            <div className="dsm-timeline-wrapper">
                                {grouped.now.map((msg, idx) => (
                                    <TimelineItem key={msg.id || idx} msg={msg} />
                                ))}
                            </div>
                        </section>
                    )}

                    {/* Section LAST HOUR */}
                    <section className={`dsm-section ${!activeSections.lastHour ? 'inactive' : ''}`}>
                        <div className="dsm-section-header" onClick={() => toggleSection('lastHour')}>
                            <span className="dsm-section-title">Last Hour</span>
                            <div className="dsm-divider"></div>
                            <span className="dsm-accordion-icon" style={{ transform: activeSections.lastHour ? 'rotate(180deg)' : 'rotate(0)' }}>▼</span>
                        </div>
                        {activeSections.lastHour && (
                            <div className="dsm-timeline-wrapper">
                                {grouped.lastHour.length === 0 ? (
                                    <p className="has-text-grey-light is-size-7 text-center">No messages in last hour</p>
                                ) : (
                                    grouped.lastHour.map((msg, idx) => (
                                        <TimelineItem key={msg.id || idx} msg={msg} />
                                    ))
                                )}
                            </div>
                        )}
                    </section>

                    {/* Section OTHERS */}
                    <section className={`dsm-section ${!activeSections.others ? 'inactive' : ''}`}>
                        <div className="dsm-section-header" onClick={() => toggleSection('others')}>
                            <span className="dsm-section-title">Others</span>
                            <div className="dsm-divider"></div>
                            <span className="dsm-accordion-icon" style={{ transform: activeSections.others ? 'rotate(180deg)' : 'rotate(0)' }}>▼</span>
                        </div>
                        {activeSections.others && (
                            <div className="dsm-timeline-wrapper" style={{ opacity: 0.75 }}>
                                {grouped.others.length === 0 ? (
                                    <p className="has-text-grey-light is-size-7 text-center">No older messages</p>
                                ) : (
                                    grouped.others.map((msg, idx) => (
                                        <TimelineItem key={msg.id || idx} msg={msg} />
                                    ))
                                )}
                            </div>
                        )}
                    </section>
                </main>

                {/* Footer */}
                <footer className="dsm-footer">
                    {!isReadOnly && (
                        <div className="dsm-status-actions">
                            <button
                                className="dsm-status-btn sos"
                                onClick={() => handleSetStatus('SOS')}
                                disabled={statusProcessing}
                            >
                                <span>🚨 SET SOS</span>
                            </button>

                            <button
                                className="dsm-status-btn ok"
                                onClick={() => handleSetStatus('OK')}
                                disabled={statusProcessing}
                            >
                                <span>✅ NORMAL</span>
                            </button>
                            <button
                                className="dsm-status-btn clear"
                                onClick={() => handleSetStatus('CLEAR')}
                                disabled={statusProcessing}
                            >
                                <span>🗑️ CLEAR</span>
                            </button>
                        </div>
                    )}

                    {!isReadOnly && (
                        <button className="dsm-ask-btn" onClick={handleAskStatus} disabled={sending}>
                            <FontAwesomeIcon icon={faSatelliteDish} />
                            <span className="tracking-wider">{sending ? 'SENDING...' : 'ASK STATUS'}</span>
                        </button>
                    )}
                </footer>
            </div>
        </div>
    );
};

const TimelineItem = ({ msg }) => {
    // If status is RECEIVED, it's incoming. otherwise it's outgoing
    const isOutgoing = msg.status !== 'RECEIVED';

    // Use the fixedDate computed in groupMessages, or fallback
    const displayDate = msg.fixedDate || new Date(msg.created_at || msg.createdAt);
    const timeStr = displayDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });

    return (
        <div className={`dsm-message-row ${!isOutgoing ? 'incoming' : ''}`}>
            {/* Thread line */}
            <div className="dsm-timeline-line"></div>

            {/* Icon */}
            <div className={`dsm-icon-circle ${isOutgoing ? 'outgoing' : 'incoming response'}`}>
                <FontAwesomeIcon icon={isOutgoing ? faUpload : faDownload} />
            </div>

            {/* Content Bubble */}
            <div className={`dsm-bubble ${isOutgoing ? 'outgoing' : 'incoming'}`}>
                <div className="dsm-bubble-header">
                    <span className={`dsm-bubble-label ${isOutgoing ? 'outgoing' : 'incoming'}`}>
                        {isOutgoing ? 'Outgoing Command' : 'Incoming Response'}
                    </span>
                    <span className="dsm-bubble-time">{timeStr}</span>
                </div>
                <p className="dsm-bubble-content">{msg.payload}</p>

                {isOutgoing && (
                    <div className="dsm-bubble-footer dsm-bubble-status-success">
                        <FontAwesomeIcon icon={faCheckCircle} />
                        <span>{(msg.status === 'SENT' || msg.status === 'DELIVERED' || msg.status === 'Array' || Array.isArray(msg.status)) ? 'Sent successfully' : msg.status}</span>
                    </div>
                )}
            </div>
        </div>
    );
};

export default React.memo(DeviceStatusModal, (prevProps, nextProps) => {
    // Only re-render if device ID changes OR important attributes change
    const p = prevProps.device;
    const n = nextProps.device;
    if (!p || !n) return false;
    
    return p.deviceId === n.deviceId &&
           p.id === n.id &&
           p.latitude === n.latitude &&
           p.isSosActive === n.isSosActive &&
           p.sos === n.sos &&
           JSON.stringify(p.attributes) === JSON.stringify(n.attributes);
});
